import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

export default function AgentsRequestPage() {
  return (
    <div className="bg-background-50/95">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 md:px-8 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-800/10 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-radial from-primary-600/10 to-transparent rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            <div className="w-full lg:w-1/2">
              <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
                / Partner With Us
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-foreground-50 leading-tight mb-6">
                Become a{' '}
                <span className="text-primary-400">TMT Partner</span>
              </h1>
              <p className="text-foreground-300 text-base md:text-lg leading-relaxed mb-5 font-body">
                Join the fastest-growing{' '}
                <strong className="text-foreground-100 font-semibold">
                  digital money transfer network
                </strong>{' '}
                across the Middle East, Africa, and Europe. As a TMT partner, you become the bridge
                between our cutting-edge digital platform and{' '}
                <strong className="text-foreground-100 font-semibold">
                  communities who trust you
                </strong>.
              </p>
              <p className="text-foreground-300 text-base leading-relaxed font-body">
                Whether you run a retail shop, a service center, or an existing money transfer
                outlet — partnering with TMT opens new revenue streams while you help people send
                and receive money faster, cheaper, and more securely than ever before.
              </p>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="relative overflow-hidden rounded-2xl border border-primary-700/20">
                <div className="absolute inset-0 bg-gradient-to-tr from-background-50/30 via-transparent to-transparent z-10" />
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20sophisticated%20business%20executives%20in%20sharp%20tailored%20suits%20shaking%20hands%20in%20luxurious%20penthouse%20boardroom%20with%20floor%20to%20ceiling%20windows%20overlooking%20city%20skyline%20at%20golden%20hour%20marble%20table%20gold%20designer%20lighting%20warm%20rich%20tones%20genuine%20partnership%20moment%20mutual%20respect%20collaboration%20exclusive%20high%20end%20atmosphere%20refined%20elegance%20editorial%20luxury%20business%20photography&width=1000&height=1200&seq=agents-hero-04&orientation=portrait&nocache=true"
                  alt="TMT Partner — Community Money Transfer Professional"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Become a Partner */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / The Opportunity
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              Why Partner With TMT?
            </h2>
            <p className="text-foreground-400 text-base font-body mt-4 max-w-2xl mx-auto">
              We don&rsquo;t just provide technology — we build lasting partnerships that grow your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-money-dollar-circle-line', title: 'Revenue Per Transaction', desc: 'Earn competitive commissions on every transfer, bill payment, and cash withdrawal processed through your location.' },
              { icon: 'ri-customer-service-2-line', title: 'Full Training & Support', desc: 'Comprehensive onboarding, dedicated account manager, and 24/7 operational support so you are never alone.' },
              { icon: 'ri-smartphone-line', title: 'Powered by Technology', desc: 'Access our partner portal and mobile tools for real-time tracking, reporting, and instant balance management.' },
              { icon: 'ri-building-2-line', title: 'Foot Traffic Growth', desc: 'Attract new walk-in customers who come for TMT services and discover your other products while they wait.' },
              { icon: 'ri-shield-check-line', title: 'Trusted Brand', desc: 'Leverage the TMT name — a regulated, compliant, and recognized brand across 14+ corridors.' },
              { icon: 'ri-rocket-2-line', title: 'Fast Onboarding', desc: 'Get your partner location live within 72 hours. Minimal paperwork, maximum speed to revenue.' },
            ].map((benefit) => (
              <div key={benefit.title} className="p-6 md:p-8 bg-background-50/90 border border-primary-800/20 rounded-xl hover:border-primary-600/40 transition-all duration-500 group">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 border border-primary-600/20 mb-5 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${benefit.icon} text-primary-400 text-xl`} />
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-2">{benefit.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Apply */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Eligibility
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              Who Can Become A Partner?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: 'ri-store-2-line', title: 'Retail Stores', desc: 'Supermarkets, convenience stores, mobile recharge shops, and neighborhood retailers.' },
              { icon: 'ri-exchange-dollar-line', title: 'Money Transfer Outlets', desc: 'Existing remittance or currency exchange businesses looking to expand service offerings.' },
              { icon: 'ri-hotel-line', title: 'Service Centers', desc: 'Business centers, travel agencies, telecom outlets, and customer service points.' },
              { icon: 'ri-user-star-line', title: 'Independent Partners', desc: 'Entrepreneurs and individuals with a registered business and a physical location.' },
            ].map((item) => (
              <div key={item.title} className="p-6 bg-background-100/90 border border-primary-800/20 rounded-xl text-center hover:border-primary-600/40 transition-all duration-500">
                <div className="w-14 h-14 mx-auto flex items-center justify-center border border-primary-600/30 rounded-full mb-4">
                  <i className={`${item.icon} text-primary-400 text-2xl`} />
                </div>
                <h3 className="font-heading text-lg text-foreground-100 mb-2">{item.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Checklist */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100/95">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3">
              / Requirements
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">
              What You Need to Get Started
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Valid business license or commercial registration',
              'Physical storefront or office location',
              'Government-issued identification',
              'Basic banking relationship (business bank account)',
              'Internet connection and a smartphone or tablet',
              'Ability to maintain a float balance for transactions',
              'Clean business record with no financial fraud history',
              'Commitment to TMT compliance and AML policies',
            ].map((req, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-background-50/90 border border-primary-800/20 rounded-lg">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500/15 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-primary-400 text-sm" />
                </div>
                <span className="text-foreground-300 text-sm leading-relaxed font-body">{req}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Redirect to Signup */}
      <section id="agent-cta" className="py-20 md:py-28 px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-4">
            / Apply Now
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50 mb-4">
            Ready to Become a TMT Partner?
          </h2>
          <p className="text-foreground-400 text-base leading-relaxed mb-3 font-body max-w-xl mx-auto">
            Visit our account registration page — select the{' '}
            <strong className="text-foreground-200 font-semibold">Business Account</strong> tab
            during signup to submit your partner application with KYC documents.
          </p>
          <p className="text-foreground-500 text-sm font-body mb-8">
            Our partnerships team reviews and responds within 48 hours of submission.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-label text-sm font-semibold tracking-wider uppercase rounded-full hover:bg-primary-400 transition-colors whitespace-nowrap">
              <i className="ri-user-add-line" />
              Open an Account Now
            </Link>
            <Link to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-primary-500 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-300 whitespace-nowrap">
              Have Questions? Contact Us
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}